/** Owned upload limit (`audioUploadCreateSchema.byteLength` max, 200 MiB). */
export const OWNED_UPLOAD_MAX_BYTES = 209_715_200;
/** Warn while recording once this share of the upload limit is used. */
export const UPLOAD_LIMIT_WARNING_RATIO = 0.9;
/** Warn when less than this much browser storage is free (1 h ≈ 11 MB). */
export const LOW_STORAGE_BYTES = 200 * 1024 * 1024;
/**
 * MediaRecorder timeslice: at most this much audio is at risk in a crash.
 * Each slice is one small IndexedDB write (about 3 KB at 24 kbit/s); a page
 * that is hidden or unloading also saves what it has at once (requestData).
 */
export const CHUNK_INTERVAL_MS = 1_000;
/** The live recorder or sheet refreshes its claim this often. */
export const HEARTBEAT_INTERVAL_MS = 10_000;
/** A claim older than this means no tab owns the recording any more. */
export const HEARTBEAT_STALE_MS = 30_000;
/** Recordings are removed from the device after this long. */
export const OFFLINE_RECORDING_TTL_MS = 7 * 24 * 60 * 60 * 1000;
/** Show the "will be deleted" warning this long before the TTL. */
export const OFFLINE_RECORDING_TTL_WARNING_MS = 2 * 24 * 60 * 60 * 1000;
/** Automatic retry back-off: base delay, doubled per attempt, capped. */
export const RETRY_BASE_MS = 5_000;
export const RETRY_MAX_MS = 5 * 60 * 1000;

export interface StorageStatus {
  /** Bytes still available to this site, when the browser reports it. */
  available: number | null;
  low: boolean;
  /** `navigator.storage.persist()` result; null when not supported. */
  persisted: boolean | null;
}

/** Reads the storage estimate; never throws. */
export const readStorageStatus = async (): Promise<StorageStatus> => {
  let available: number | null = null;
  let persisted: boolean | null = null;
  try {
    const estimate = await navigator.storage?.estimate?.();
    if (
      estimate &&
      typeof estimate.quota === "number" &&
      typeof estimate.usage === "number"
    )
      available = Math.max(0, estimate.quota - estimate.usage);
  } catch {}
  try {
    if (navigator.storage?.persisted)
      persisted = await navigator.storage.persisted();
  } catch {}
  return {
    available,
    low: available !== null && available < LOW_STORAGE_BYTES,
    persisted,
  };
};

let persistRequest: Promise<boolean | null> | null = null;
/**
 * Asks the browser not to evict recordings under disk pressure (once per
 * page). Chrome grants it silently for engaged sites; never throws.
 */
export const requestPersistentStorage = (): Promise<boolean | null> => {
  persistRequest ??= (async () => {
    try {
      if (!navigator.storage?.persist) return null;
      if (await navigator.storage.persisted?.()) return true;
      return await navigator.storage.persist();
    } catch {
      return null;
    }
  })();
  return persistRequest;
};

export const resetPersistRequestForTests = (): void => {
  persistRequest = null;
};

export const retryDelay = (attempts: number): number =>
  Math.min(RETRY_MAX_MS, RETRY_BASE_MS * 2 ** Math.max(0, attempts - 1));

export const expiresAt = (createdAt: number): number =>
  createdAt + OFFLINE_RECORDING_TTL_MS;

export const isExpired = (createdAt: number, now = Date.now()): boolean =>
  now >= expiresAt(createdAt);

export const isExpiringSoon = (createdAt: number, now = Date.now()): boolean =>
  !isExpired(createdAt, now) &&
  expiresAt(createdAt) - now <= OFFLINE_RECORDING_TTL_WARNING_MS;
